/**
 * Substitute the `{{target}}` placeholder in resolved theory copy with a
 * suggestion's resolution-target roman numeral (e.g. `vi`, `V`).
 *
 * The `secondary-dominant` lexicon entry (and its `tagLabel`) is parameterized
 * on `{{target}}` so a single entry covers all four offered secondary dominants
 * (V/ii, V/IV, V/V, V/vi) — see
 * `lib/music-lib/src/lexicon/lexicon-secondary-dominant-entries.ts`. The copy
 * is interpolated at the use site, BEFORE display, by this helper.
 *
 * Pure and total. Two honesty guards (brief §4 — never render a riddle):
 *   - When `target` is absent (undefined / empty), the placeholder is STRIPPED,
 *     not left raw — a user must never see the literal `{{target}}` token. The
 *     copy degrades to a slightly more generic but still true sentence.
 *   - When the copy carries no placeholder, it is returned untouched.
 *
 * @example
 *   interpolateTarget('leads to {{target}}', 'vi')        // → 'leads to vi'
 *   interpolateTarget('leads to {{target}}', undefined)   // → 'leads to'  (no raw token)
 *   interpolateTarget('plain copy', 'V')                  // → 'plain copy'
 */
export const interpolateTarget = (copy: string, target: string | undefined): string => {
  const replacement = target && target.length > 0 ? target : ''
  const result = copy.split('{{target}}').join(replacement)
  // Collapse any double-spacing / dangling space left when the placeholder was
  // stripped (e.g. `V7/{{target}}` → `V7/`, `tonicizes {{target}}` → `tonicizes`).
  return result.replace(/\s+/g, ' ').trim()
}
